import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { gatorProtectActions } from '@/redux/modules/gatorProtect';
import {
  PlansBanner,
  ProtectionCard,
  Summary,
} from './components';
import ChartSection from './sections/ChartSection/ChartSection';
import ThreatsTableSection from './sections/ThreatsTableSection/ThreatsTableSection';
import * as Styles from './GatorProtectHandler.styles';

import useLocale from '@/hooks/useLocale';
import withErrorBoundary from '@/utils/errorBoundary';
import ProtectionModal from './components/ProtectionModal';
import { productsActions } from '@/redux/modules/products';
import { GATOR_PROTECT_ID } from './GatorProtect.enum';
import { parseGatorProtectUpgradeHostingId } from '@/redux/modules/gatorProtect/gatorProtect.buy.utils';
import { formatCurrency } from '@/utils/Formatters/formatCurrency';
import { getIsoDateStringDaysAgo } from '@/utils/date-time/getIsoDateStringDaysAgo';
import { useHistory } from 'react-router';
import { getSelectedPlanInfo } from './utils/getSelectedPlanInfo';

const TRIAL_ACTIVATE = 'TrialActivate';
const PRO_ACTIVATE = 'ProActivate';

const GatorProtectHandler = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const {
    gatorProtect: gatorProtectLocale,
    routes: routesLocale,
  } = useLocale();
  const { loading, plans, planDetails } = useSelector(state => state.gatorProtect);
  const { loading: fastCheckoutLoading } = useSelector(state => state.fastCheckout);
  const summary = useSelector(state => state.summary);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [protectionModal, setProtectionModal] = useState({
    open: false,
    activationKey: null,
    agreed: false,
    acceptedTermsProduct: null,
  });
  const { consultedProducts } = useSelector(state => state.products);
  const summaryState = useSelector(state => state.summary);
  const gatorProtectProData = consultedProducts?.find(item => item.id === GATOR_PROTECT_ID.PRO);

  const {
    isTrialActive,
    isTrialExpired,
    isProActive,
    isAAClient,
    hasClientUsedTrial,
    isTurbo,
    hasInvoice,
    invoiceId,
  } = getSelectedPlanInfo(selectedPlan);

  const invoiceHref = `${routesLocale.invoices}${routesLocale.unpaid}/${invoiceId}`;

  const getProPrice = useCallback(() => {
    dispatch(productsActions.products.getProductById(GATOR_PROTECT_ID.PRO));
  }, [dispatch]);

  useEffect(() => { getProPrice(); }, [getProPrice]);

  const getPlansByServer = useCallback(() => {
    dispatch(gatorProtectActions.plans.request({ headerId: null }));
  }, [dispatch]);

  useEffect(() => { getPlansByServer(); }, [getPlansByServer]);
  const preventAccessIfClientCantSeeGatorProtect = useCallback(() => {
    if (!summary?.features?.gator_protect) {
      history.push(routesLocale.home);
    }
  }, [history, routesLocale, summary]);

  useEffect(() => {
    preventAccessIfClientCantSeeGatorProtect();
  }, [preventAccessIfClientCantSeeGatorProtect]);

  const handleOnChangePlan = (plan) => {
    setSelectedPlan(plan);
    const formattedStartDate = getIsoDateStringDaysAgo(30);
    dispatch(gatorProtectActions.planDetails.request({
      hostingId: plan.shared_hosting_id,
      startDate: formattedStartDate,
    }));
  };

  const closeProtectionModal = () => {
    setProtectionModal({ open: false, activationKey: null, agreed: false });
  };

  const openProtectionProModal = () => {
    setProtectionModal({
      open: true, activationKey: 'activateProtectionPro', agreed: false, acceptedTermsProduct: PRO_ACTIVATE,
    });
  };

  const openProtectionTrialModal = () => {
    setProtectionModal({
      open: true, activationKey: 'activateTrial', agreed: false, acceptedTermsProduct: TRIAL_ACTIVATE,
    });
  };

  const handleToggleAgreed = () => {
    setProtectionModal(prev => ({ ...prev, agreed: !prev.agreed }));
  };

  const handleTermsAcceptance = () => {
    const hostingId = selectedPlan?.shared_hosting_id;
    if (hostingId == null || !protectionModal.agreed) {
      return;
    }
    dispatch(gatorProtectActions.acceptTerms.request({
      hostingId,
      termsAccepted: protectionModal.agreed ? protectionModal.acceptedTermsProduct : null,
    }));
  };

  const protectionModalContent = useMemo(() => {
    const modalContent = gatorProtectLocale
      && gatorProtectLocale.summary
      && gatorProtectLocale.summary.protectionModal;

    if (!modalContent) {
      return null;
    }

    const getContent = () => {
      const renderNewProtectionContent = !isTrialExpired && protectionModal?.activationKey === 'activateProtectionPro';

      if (renderNewProtectionContent) {
        // proActivation flow
        const content = {
          ...modalContent.proActivation,
          buttonLabel: typeof modalContent?.proActivation?.buttonLabel === 'function'
            ? modalContent.proActivation.buttonLabel(gatorProtectProData?.prices?.pricing?.monthly ? formatCurrency(gatorProtectProData?.prices?.pricing?.monthly) : '...')
            : modalContent.proActivation.buttonLabel
          ,
        };

        if (isTurbo) {
          content.buttonLabel = typeof modalContent?.proActivation?.buttonLabel === 'function'
            ? modalContent.proActivation.buttonLabel(null)
            : modalContent.proActivation.buttonLabel;
        }

        return {
          ...content,
        };
      }

      if (isTrialActive) {
        return {
          ...modalContent.trialActive,
          buttonLabel:
            typeof modalContent?.trialActive?.buttonLabel === 'function'
              ? modalContent.trialActive.buttonLabel(gatorProtectProData?.prices?.pricing?.monthly ? formatCurrency(gatorProtectProData?.prices?.pricing?.monthly) : '...')
              : modalContent.trialActive.buttonLabel,
        };
      }

      if (isTrialExpired) {
        return modalContent.trialActive;
      }

      return {
        ...modalContent.trialActivation,
        title: modalContent.trialActivation.title(isAAClient),
        subtitle: modalContent.trialActivation.subtitle(gatorProtectProData?.prices?.pricing?.monthly ? formatCurrency(gatorProtectProData?.prices?.pricing?.monthly) : '...'),
      };
    };

    const scenario = getContent();
    const parsedMessage = typeof scenario.message === 'function'
      ? scenario.message((selectedPlan && selectedPlan.expire_days) || 0)
      : scenario.message;

    const commonContent = modalContent.common || {};

    return {
      ...scenario,
      message: parsedMessage,
      checklistItems: scenario.checklistItems || commonContent.checklistItems || [],
      productTitle: commonContent.productTitle || gatorProtectLocale.banner.title,
      productSubtitle: commonContent.productSubtitle || gatorProtectLocale.banner.description,
      proBadgeLabel: commonContent.proBadgeLabel || gatorProtectLocale.banner.proBadgeLabel,
      mascotAlt: gatorProtectLocale.banner.img.alt,
    };
  }, [gatorProtectLocale, isTrialActive, selectedPlan, isTrialExpired, gatorProtectProData, isAAClient, protectionModal, isTurbo]);

  const handleAutomaticCleaningToggle = () => {
    const current = planDetails.auto_protect ?? true;
    const next = !current;
    dispatch(gatorProtectActions.planDetails.setAutoProtect(next));
    if (selectedPlan?.shared_hosting_id != null) {
      dispatch(gatorProtectActions.planDetails.updateAutoProtect.request({
        hostingId: selectedPlan.shared_hosting_id,
        autoProtect: next,
      }));
    }
  };

  const getSelectedPlanDetails = useCallback(() => {
    if (plans.length > 0 && selectedPlan === null) {
      const plan = plans[0];
      setSelectedPlan(plan);
      const formattedStartDate = getIsoDateStringDaysAgo(30);
      dispatch(gatorProtectActions.planDetails.request({
        hostingId: plan.shared_hosting_id,
        startDate: formattedStartDate,
      }));
    }
  }, [plans, selectedPlan, dispatch]);

  useEffect(() => { getSelectedPlanDetails(); }, [getSelectedPlanDetails]);

  const handleActivate = (activationKey) => {
    const redirectToInvoice = (invoiceId) => {
      history.push(`${routesLocale.invoices}${routesLocale.unpaid}/${invoiceId}`);
    };

    switch (activationKey) {
      case 'activateTrial': {
        dispatch(gatorProtectActions.purchase.fastCheckout.request({
          productId: GATOR_PROTECT_ID.TRIAL,
          domain: selectedPlan?.shared_hosting_domain,
          hostingId: selectedPlan?.shared_hosting_id,
          redirectToInvoice: false,
          onPurchaseSuccess: () => {
            setSelectedPlan({
              ...selectedPlan,
              is_trial: true,
              expire_days: isAAClient ? 365 : 30,
            });
            setProtectionModal({
              open: false,
              activationKey: null,
              agreed: false,
            });
            dispatch(gatorProtectActions.plans.request({ headerId: null }));
            const formattedStartDate = getIsoDateStringDaysAgo(30);
            dispatch(gatorProtectActions.planDetails.request({
              hostingId: selectedPlan.shared_hosting_id,
              startDate: formattedStartDate,
            }));
          },
        }));
        break;
      }
      case 'activateProtectionPro': {
        const upgradeHostingId = parseGatorProtectUpgradeHostingId(selectedPlan?.gatorprotect_hosting_id);
        if (upgradeHostingId != null) {
          dispatch(gatorProtectActions.purchase.upgradeOrder.request({
            hostingId: upgradeHostingId,
            newProductId: GATOR_PROTECT_ID.PRO,
            newBillingCycle: 'monthly',
            redirectToInvoice,
          }));
        } else {
          dispatch(gatorProtectActions.purchase.fastCheckout.request({
            productId: GATOR_PROTECT_ID.PRO,
            domain: selectedPlan?.shared_hosting_domain,
            redirectToInvoice,
            hostingId: selectedPlan?.shared_hosting_id,
          }));
        }
        break;
      }
      default:
        break;
    }
  };

  const preventAccess = useCallback(() => {
    if (!summaryState?.features?.gator_protect) {
      history.push(routesLocale.home);
    }
  }, [history, routesLocale, summaryState]);

  useEffect(() => {
    preventAccess();
  }, [preventAccess]);

  const getSortedChartByDate = useCallback(() => {
    if (!planDetails.activity_detected) {
      return [];
    }
    const compareByDateAscending = (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime();
    return [...planDetails.activity_detected].sort(compareByDateAscending);
  }, [planDetails.activity_detected]);

  return (
    <Styles.Container data-testid="gator-protect-handler">
      <div>
        <PlansBanner
          content={gatorProtectLocale.banner}
          plans={plans}
          loading={loading || plans.length === 0}
          selectedPlan={selectedPlan}
          onChangePlan={handleOnChangePlan}
        />
        <Summary content={gatorProtectLocale.summary} loading={loading}>
          {({ Banners, Main, Alerts }) => (
            <>
              <Banners>
                {isTrialActive && <Summary.Banner type="trialActive" trialDaysLeft={(selectedPlan && selectedPlan.expire_days) || 0} />}
                {isProActive && <Summary.Banner type="fullProtectionActive" />}
                {isTrialExpired && <Summary.Banner type="expiredProtection" />}
              </Banners>
              <Main>
                <Summary.Title
                  loading={loading}
                  enabled={isProActive ? (planDetails.auto_protect ?? true) : undefined}
                  onToggle={isProActive ? handleAutomaticCleaningToggle : undefined}
                />
                <Styles.DataAndMetricsRow autoColumns={isProActive}>
                  <Summary.Metrics
                    maliciousCodesRemoved={planDetails?.summary?.malicious}
                    compromisedFiles={planDetails?.summary?.compromised}
                  />

                  {!isProActive && (
                    <ProtectionCard
                      isLoading={loading}
                      hasInvoice={hasInvoice}
                      isTrialActive={isTrialActive}
                      isTrialExpired={isTrialExpired}
                      onActivateProtectionPro={openProtectionProModal}
                      onActivateTrial={openProtectionTrialModal}
                      onPendingInvoiceClick={() => history.push(invoiceHref)}
                      isAAClient={isAAClient}
                      hideActivateTrial={isTrialActive || hasClientUsedTrial}
                      isTurbo={isTurbo}
                      gatorProtectProPrice={gatorProtectProData?.prices?.pricing?.monthly ? formatCurrency(gatorProtectProData?.prices?.pricing?.monthly) : null}
                    />
                  )}
                </Styles.DataAndMetricsRow>
              </Main>
              <Alerts />
            </>
          )}
        </Summary>
      </div>
      <ChartSection
        sectionId="activities-chart"
        data={getSortedChartByDate()}
        isLoading={loading}
      />
      <ThreatsTableSection />
      {(protectionModal.open && protectionModalContent) && (
        <ProtectionModal
          content={protectionModalContent}
          agreed={protectionModal.agreed}
          onToggleAgreed={handleToggleAgreed}
          onButtonClick={() => {
            handleTermsAcceptance();
            handleActivate(protectionModal.activationKey);
          }}
          onClose={closeProtectionModal}
          terms={gatorProtectLocale.terms}
          loading={loading || !consultedProducts?.length}
          fastCheckoutLoading={fastCheckoutLoading}
        />
      )}
    </Styles.Container>
  );
};

export default withErrorBoundary(GatorProtectHandler, 'GatorProtectHandler');
